// MUI
import { Stack } from "@mui/material";

// Components
import SmartSelector from "@Layouts/components/InputComponents/SmartSelector";
import Title from "@Settings/components/SystemSettings/common/Title";
import InfoSubTitle from "@Settings/components/SystemSettings/common/InfoSubTitle";
import SimpleTextField from "@Layouts/components/InputComponents/SimpleTextField";
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";

// Types
import { IAllowableTimeAway } from "@Settings/types/administrationTools/model";
import { TSelectValue } from "@Layouts/components/InputComponents/SmartSelector/interfaces";
import { useEffect, useState } from "react";

type TAllowableTimeAwayAndChargebackAmountProps = Readonly<{
	isLoading: boolean;
	providerDBProperty: string;
	allowableTimeAway: IAllowableTimeAway;
	chargebackAmountValue: number;
	// Callbacks
	onActionUpdate: (value: string | TSelectValue, prop: "timeAway" | "unit" | "chargebackAmount", providerDBProperty: string) => void;
}>;

const COMMON_CLASSNAME = "!mb-[12px]";

export default function AllowableTimeAwayAndChargebackAmount(props: TAllowableTimeAwayAndChargebackAmountProps) {
	const { allowableTimeAway, chargebackAmountValue, isLoading, providerDBProperty, onActionUpdate } = props;

	const [chargebackAmount, setChargebackAmount] = useState<number>(0);

	const availableUnits = [
		{ _id: "days", label: "Days" },
		{ _id: "hours", label: "Hours" },
		{ _id: "minutes", label: "Minutes" },
	];

	useEffect(() => {
		if (chargebackAmountValue !== chargebackAmount) {
			setChargebackAmount(chargebackAmountValue);
		}
	}, [chargebackAmountValue]);

	return (
		<Stack spacing={2}>
			<Title title={"Allowable Time Away and Key Chargeback Amount"} />
			<Stack spacing={4} direction={"row"}>
				<Stack>
					<InfoSubTitle title={"Time Away"} className={COMMON_CLASSNAME} />
					<SimpleTextField
						placeholder="Amount"
						text={allowableTimeAway?.timeAway}
						sx={{ width: "175px" }}
						editable
						onlyNumbers
						outlined
						disabled={isLoading}
						// Callbacks
						onActionUpdate={(value) => onActionUpdate(value, "timeAway", providerDBProperty)}
					/>
				</Stack>
				<Stack>
					<InfoSubTitle title={"Unit"} className={COMMON_CLASSNAME} />
					<SmartSelector
						options={availableUnits}
						selected={allowableTimeAway?.unit}
						loading={isLoading}
						inputProps={{ placeholder: "Unit", classes: "!w-[100px]" }}
						// Callbacks
						onChangeAction={(value) => onActionUpdate(value, "unit", providerDBProperty)}
					/>
				</Stack>
				<Stack>
					<InfoSubTitle title={"Chargeback Amount"} className={COMMON_CLASSNAME} />
					<InputFormExtension
						data={[
							{
								type: "Dollar",
								min: 0,
								max: Number.MAX_VALUE,
								decimalsEditable: true,
								decimals: 2,
								delta: 0.01,
							},
						]}
						value={chargebackAmount ?? ""}
						defaultType={"Dollar"}
						onActionUpdate={(value) => {
							setChargebackAmount(value as number);
							onActionUpdate(value as unknown as TSelectValue, "chargebackAmount", providerDBProperty);
						}}
						sx={{
							width: "95px",
						}}
					/>
				</Stack>
			</Stack>
		</Stack>
	);
}
